import React, { useEffect, useRef, useState, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Input,
  ListGroup,
  ListGroupItem,
  CardHeader,
  Modal,
  ModalHeader,
  ModalBody,
  Form,
  FormGroup,
} from "reactstrap";
import Swal from "sweetalert2";
import Header from "components/Headers/Header";
import { useSessionStore } from "../../stores/sessionStore";
import { useSettingStore } from "../../stores/settingStore";
import { api } from "../../config/middleware";
import {
  API_GETSERVICEPACKAGES,
  API_BUYSERVICEPACKAGE,
  API_GETCUSTOMERWITHPACKAGE,
  API_ADDCUSTOMER,
  API_GETSETTING,
} from "../../config/api";
import LoadingSpinner from "components/LoadingSpinner";

const OrderBuyPackage = () => {
  const sa = {
    info: (text, title = "แจ้งเตือน") =>
      Swal.fire({ icon: "info", title, text, confirmButtonText: "ตกลง" }),
    success: (text, title = "สำเร็จ") =>
      Swal.fire({
        icon: "success",
        title,
        text,
        showConfirmButton: false,
        timer: 2000,
      }),
    error: (text, title = "เกิดข้อผิดพลาด") =>
      Swal.fire({ icon: "error", title, text, confirmButtonText: "ปิด" }),
  };

  const navigate = useNavigate();

  const orgId = useSessionStore((s) => s.profile?.orgId);
  const tnId = useSessionStore((s) => s.profile?.tnId);
  const brId = useSessionStore((s) => s.profile?.brId);

  // (ถ้าคุณยังต้องใช้ settingStore อยู่ก็เก็บไว้, ไม่ใช้ก็ลบทั้งบล็อกนี้ได้)
  const setBillMode = useSettingStore((s) => s.setBillMode);

  useEffect(() => {
    const fetchSetting = async () => {
      const res = await api.get(API_GETSETTING, {
        params: { TNID: tnId, ORGID: orgId, BRID: brId },
      });

      if (res.data?.statustext === "SUCCESS") {
        setBillMode(res.data.data?.billMode);
      }
    };

    if (tnId != null && orgId != null && brId != null) {
      fetchSetting();
    }
  }, [tnId, orgId, brId, setBillMode]);

  // ===============================
  // Customer Search (Package)
  // ===============================
  const buildCustomerQuery = (term) => {
    const t = String(term || "").trim();
    if (!t) return null;
    if (/^CUS/i.test(t)) return { CMCODE: t };
    if (/^[0-9]+$/.test(t)) return { CMMOBILE: t };
    return { CMNAME: t };
  };

  const [selectedCustomerPackage, setSelectedCustomerPackage] = useState(null);

  const SearchBoxBase = ({ inputId, onPick }) => {
    const inputRef = useRef(null);
    const debounceRef = useRef(null);
    const reqCounter = useRef(0);

    const [isSearching, setIsSearching] = useState(false);
    const [cusErr, setCusErr] = useState("");
    const [searchResults, setSearchResults] = useState([]);

    const fireSearch = async (raw) => {
      const term = String(raw || "").trim();
      setCusErr("");

      if (!orgId || !term) {
        setSearchResults([]);
        setIsSearching(false);
        return;
      }

      const q = buildCustomerQuery(term);
      if (!q) {
        setSearchResults([]);
        setIsSearching(false);
        return;
      }

      const myReq = ++reqCounter.current;
      setIsSearching(true);

      try {
        const res = await api.post(API_GETCUSTOMERWITHPACKAGE, {
          ORGID: orgId,
          ...q,
        });

        if (myReq !== reqCounter.current) return;

        const arr = Array.isArray(res?.data?.data) ? res.data.data : [];

        const termLower = term.toLowerCase();
        const filtered = arr.filter((r) => {
          const code = String(r.cmCode || "").toLowerCase();
          const name = String(r.cmName || "").toLowerCase();
          const mobile = String(r.cmMobile || "").toLowerCase();
          const phone = String(r.cmPhone || "").toLowerCase();
          return (
            code.includes(termLower) ||
            name.includes(termLower) ||
            mobile.includes(termLower) ||
            phone.includes(termLower)
          );
        });

        const rows = filtered.map((r) => ({
          id: r.cmCode || r.id,
          code: r.cmCode || "",
          name: r.cmName || "",
          mobile: r.cmMobile || "",
          phone: r.cmPhone || "",
          subscriptions: r.subscriptions || [],
          raw: r,
        }));

        setSearchResults(rows.slice(0, 10));
      } catch (e) {
        if (myReq !== reqCounter.current) return;
        setSearchResults([]);
        setCusErr(e?.response?.data?.message || e?.message || "ค้นหาไม่สำเร็จ");
      } finally {
        if (reqCounter.current === myReq) setIsSearching(false);
      }
    };

    const onChange = (e) => {
      const v = e.target.value ?? "";
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => fireSearch(v), 350);
    };

    return (
      <div style={{ position: "relative", width: "100%" }}>
        <div className="input-group w-100">
          <input
            id={inputId}
            ref={inputRef}
            type="text"
            className="form-control"
            placeholder="รหัสลูกค้า/ชื่อ/เบอร์โทร"
            autoComplete="off"
            spellCheck={false}
            onChange={onChange}
            onKeyDown={(e) => {
              if (e.key === "Enter") e.preventDefault();
            }}
          />
          <div className="input-group-append">
            <button
              type="button"
              onClick={() => setIsAddModalOpen(true)}
              className="btn"
              style={{
                backgroundColor: "#0ea5e9",
                color: "#fff",
                border: "none",
              }}
              title="เพิ่มลูกค้าใหม่"
            >
              <i className="ni ni-fat-add" />
            </button>
          </div>
        </div>

        {isSearching && (
          <div
            className="shadow"
            style={{
              position: "absolute",
              zIndex: 20,
              top: "100%",
              left: 0,
              right: 0,
              background: "#fff",
              borderRadius: 12,
              border: "1px solid #e5e7eb",
              marginTop: 6,
              padding: "10px 12px",
            }}
          >
            กำลังค้นหา…
          </div>
        )}

        {!isSearching && cusErr && (
          <div
            className="shadow text-danger"
            style={{
              position: "absolute",
              zIndex: 20,
              top: "100%",
              left: 0,
              right: 0,
              background: "#fff",
              borderRadius: 12,
              border: "1px solid #e5e7eb",
              marginTop: 6,
              padding: "10px 12px",
            }}
          >
            {cusErr}
          </div>
        )}

        {searchResults.length > 0 && !isSearching && (
          <div
            className="shadow"
            style={{
              position: "absolute",
              zIndex: 20,
              top: "100%",
              left: 0,
              right: 0,
              background: "#fff",
              borderRadius: 12,
              border: "1px solid #e5e7eb",
              marginTop: 6,
              overflow: "hidden",
            }}
          >
            {searchResults.map((c) => (
              <div
                key={c.id}
                onMouseDown={(e) => {
                  e.preventDefault();
                  onPick(c);
                  setSearchResults([]);
                  if (inputRef.current)
                    inputRef.current.value = `${c.code} - ${c.name}`;
                }}
                style={{
                  padding: "12px 14px",
                  cursor: "pointer",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <div style={{ fontWeight: 700 }}>
                  {c.code ? `${c.code} - ` : ""}
                  {c.name}
                </div>
                <div style={{ fontSize: 12, color: "#64748b" }}>
                  {c.mobile || c.phone || ""}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const SearchBoxPackage = (props) => (
    <SearchBoxBase
      {...props}
      onPick={(cus) => setSelectedCustomerPackage(cus)}
    />
  );

  // ===============================
  // Packages
  // ===============================
  const [servicePkgs, setServicePkgs] = useState([]);
  const [cartPackage, setCartPackage] = useState([]);
  const [spLoading, setSpLoading] = useState(false);
  const [spError, setSpError] = useState("");
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    const fetchPkgs = async () => {
      if (!orgId) return;
      setSpLoading(true);
      setSpError("");
      try {
        const res = await api.get(API_GETSERVICEPACKAGES, {
          params: { ORGID: orgId },
        });
        const arr = Array.isArray(res?.data?.data) ? res.data.data : [];
        const normalized = arr.map((x) => ({
          id: x.svId,
          code: x.svCode,
          name: x.svName,
          price: Number(x.svPrice ?? 0),
          unit: x.svUnit || "ครั้ง",
          qty: Number(x.svQty ?? 0),
          leadtime: Number(x.svLeadTime ?? 0),
          type: x.svType,
        }));
        setServicePkgs(normalized);
        setCartPackage([]);
      } catch (e) {
        setSpError(e?.message || "โหลดแพ็คเกจไม่สำเร็จ");
      } finally {
        setSpLoading(false);
      }
    };
    fetchPkgs();
  }, [orgId]);

  const totalPackage = useMemo(
    () => cartPackage.reduce((s, p) => s + p.price * p.qty, 0),
    [cartPackage],
  );

  const addPkgToCart = (pkg) => {
    setCartPackage((prev) => {
      const found = prev.find((it) => String(it.id) === String(pkg.id));
      if (found) {
        return prev.map((it) =>
          String(it.id) === String(pkg.id)
            ? { ...it, qty: Number(it.qty || 0) + 1 }
            : it,
        );
      }
      return [
        ...prev,
        { id: pkg.id, name: pkg.name, price: pkg.price, qty: 1 },
      ];
    });
  };

  const removePackageItem = (id) => {
    setCartPackage((prev) => prev.filter((x) => String(x.id) !== String(id)));
  };

  const fetchCustomerWithPackageByCode = async (cmCode) => {
    const res = await api.post(API_GETCUSTOMERWITHPACKAGE, {
      ORGID: orgId,
      CMCODE: cmCode,
    });
    const row = Array.isArray(res?.data?.data) ? res.data.data[0] : null;
    if (!row) return null;
    return {
      id: row.cmCode || row.id,
      code: row.cmCode || "",
      name: row.cmName || "",
      mobile: row.cmMobile || "",
      phone: row.cmPhone || "",
      subscriptions: row.subscriptions || [],
      raw: row,
    };
  };

  const handlePayPackages = async () => {
    if (!orgId) return sa.error("ไม่พบ ORGID");
    if (!selectedCustomerPackage?.code)
      return sa.info("กรุณาเลือกลูกค้าก่อนทำรายการ");
    if (cartPackage.length === 0) return sa.info("ยังไม่มีแพ็คเกจในตะกร้า");

    // cart → ITEMS (ส่ง SVID ซ้ำตาม qty)
    const ITEMS = [];
    cartPackage.forEach((p) => {
      const n = Math.max(1, Number(p.qty || 0));
      for (let i = 0; i < n; i += 1) ITEMS.push({ SVID: p.id });
    });

    const payload = {
      ORGID: orgId,
      ITEMS,
      CMCODE: selectedCustomerPackage.code,
    };

    try {
      setPaying(true);

      const res = await api.post(API_BUYSERVICEPACKAGE, payload);
      const ok = res?.data?.statustext === "SUCCESS";

      if (!ok) {
        console.error("BuyServicePackage error:", res?.data);
        return sa.error(res?.data?.message || "ทำรายการไม่สำเร็จ");
      }

      setCartPackage([]);

      // รีเฟรชเครดิตลูกค้า (ถ้าต้องการให้ UI update)
      const refreshed = await fetchCustomerWithPackageByCode(
        selectedCustomerPackage.code,
      );
      if (refreshed) setSelectedCustomerPackage(refreshed);

      await sa.success(res?.data?.message || "ซื้อ Subscription สำเร็จ!");
    } catch (e) {
      console.error("BuyServicePackage exception:", e);
      sa.error(e?.response?.data?.message || e?.message || "เกิดข้อผิดพลาด");
    } finally {
      setPaying(false);
    }
  };

  // ===============================
  // Add Customer Modal
  // ===============================
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [customerType, setCustomerType] = useState("individual"); // "individual" | "juristic"
  const [submittingCustomer, setSubmittingCustomer] = useState(false);
  const [customerForm, setCustomerForm] = useState({
    cmName: "",
    cmMobile: "",
  });

  const isCompany = customerType === "juristic";

  const handleChangeCustomerForm = (e) => {
    const { name, value } = e.target;
    setCustomerForm((prev) => ({ ...prev, [name]: value }));
  };

  const toggleAddModal = () => setIsAddModalOpen((v) => !v);

  const appendStr = (fd, key, val) => {
    if (val === undefined || val === null) return;
    fd.append(key, String(val));
  };

  const handleAddCustomerSubmit = async (e) => {
    e.preventDefault();
    if (submittingCustomer) return;

    try {
      if (!orgId || !tnId) return sa.error("ไม่พบ ORGID/TNID");
      if (!customerForm.cmName.trim())
        return sa.info("กรุณากรอกชื่อ/ชื่อลูกค้า");
      if (!customerForm.cmMobile.trim()) return sa.info("กรุณากรอกเบอร์โทร");

      setSubmittingCustomer(true);

      const safeDigits = (v = "") => String(v).replace(/[^0-9]/g, "");
      const fd = new FormData();

      appendStr(fd, "TNID", tnId);
      appendStr(fd, "ORGID", orgId);

      const CMTYPE_LABEL =
        customerType === "individual" ? "บุคคลธรรมดา" : "นิติบุคคล";
      appendStr(fd, "CMTYPE", CMTYPE_LABEL);

      appendStr(fd, "CMNAME", customerForm.cmName.trim());
      appendStr(fd, "CMMOBILE", safeDigits(customerForm.cmMobile));

      // ใส่ค่าอื่นว่าง ๆ ให้เหมือนของเดิม (backend บางตัว require key)
      [
        "CMADDR",
        "CMSUBDISTRICT",
        "CMDISTRICT",
        "CMPROVINCE",
        "CMZIPCODE",
        "CMBILLNAMETH",
        "CMBILLNAMEEN",
        "CBID",
        "CMTAXID",
        "CMEMAIL",
        "CMADDR2",
        "CMBILLADDR",
        "CMBILLADDR2",
        "CMBILLSUBDISTRICT",
        "CMBILLDISTRICT",
        "CMBILLPROVINCE",
        "CMBILLZIPCODE",
        "CMBILLMOBILE",
        "CMBILLEMAIL",
        "CMCONTACTNAME",
        "CMCONTACTMOBILE",
      ].forEach((k) => appendStr(fd, k, ""));

      const res = await api.post(API_ADDCUSTOMER, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      await sa.success(res?.data?.message || "เพิ่มลูกค้าสำเร็จ");

      setCustomerForm({ cmName: "", cmMobile: "" });
      setIsAddModalOpen(false);
    } catch (err) {
      console.error("AddCustomer error:", err);
      sa.error(
        err?.response?.data?.message || err?.message || "เพิ่มลูกค้าไม่สำเร็จ",
      );
    } finally {
      setSubmittingCustomer(false);
    }
  };

  if (spLoading) {
    return <LoadingSpinner fullPage />;
  }

  return (
    <>
      <style>{`
        .navbar-top, #navbar-main { z-index: 2000 !important; }
        .navbar-top .dropdown-menu, #navbar-main .dropdown-menu { z-index: 2100 !important; }
        .cart-sticky { z-index: auto !important; }
      `}</style>

      <div className="order-page">
        <Header />
        <Container className="mt--7" fluid>
          <Card>
            <CardHeader
              className="bg-white border-0"
              style={{ padding: "1.25rem 1.5rem" }}
            >
              <Row className="align-items-center">
                <Col xs="12" lg="6">
                  <Button
                    color="success"
                    onClick={() => navigate("/order-bill-org")}
                  >
                    เปิดบิล
                  </Button>
                </Col>
                <Col xs="12" lg="6" className="mt-3 mt-lg-0">
                  <div className="d-flex justify-content-lg-end">
                    <div className="w-100">
                      <label
                        htmlFor="customer-search-package"
                        className="form-label mb-1"
                        style={{ fontWeight: 600, color: "#334155" }}
                      >
                        ค้นหาลูกค้า
                      </label>
                      <SearchBoxPackage inputId="customer-search-package" />
                    </div>
                  </div>
                </Col>
              </Row>
            </CardHeader>

            <CardBody
              className="border-0 p-2 p-lg-4"
              style={{ backgroundColor: "#f7f9fb" }}
            >
              <Row className="g-0 g-lg-4 px-0 px-lg-3">
                {/* Middle: Package list */}
                <Col xs="12" md="12" lg={8} className="mb-3 px-0 px-lg-2">
                  <Card className="shadow-sm h-100">
                    <CardBody style={{ padding: "1rem" }}>
                      {!spLoading && spError && (
                        <div className="text-center text-danger py-3">
                          {spError}
                        </div>
                      )}
                      {!spLoading && !spError && servicePkgs.length === 0 && (
                        <div className="text-center text-muted py-3">
                          ยังไม่มีแพ็คเกจ
                        </div>
                      )}

                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns:
                            "repeat(auto-fit, minmax(260px, 1fr))",
                          gap: 16,
                        }}
                      >
                        {servicePkgs.map((p) => (
                          <Card
                            key={p.id}
                            onClick={() => addPkgToCart(p)}
                            role="button"
                            tabIndex={0}
                            style={{
                              borderRadius: 14,
                              backgroundColor: "#0ea5e9",
                              color: "#fff",
                              cursor: "pointer",
                              transition:
                                "transform .08s ease, box-shadow .2s ease, background-color .2s ease",
                              boxShadow: "0 4px 10px rgba(17, 113, 239, 0.3)",
                            }}
                          >
                            <CardBody style={{ padding: "16px 18px" }}>
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                }}
                              >
                                <div>
                                  <div
                                    style={{ fontWeight: 700, fontSize: 16 }}
                                  >
                                    {p.name}
                                  </div>
                                  <div
                                    style={{
                                      fontSize: 12,
                                      opacity: 0.9,
                                      marginTop: 4,
                                    }}
                                  >
                                    {p.unit ? `(${p.qty} ${p.unit})` : ""} •{" "}
                                    {p.type}
                                  </div>
                                </div>
                                <div style={{ textAlign: "right" }}>
                                  <div style={{ fontWeight: 700 }}>
                                    {p.price.toLocaleString()} ฿
                                  </div>
                                  {p.leadtime ? (
                                    <div style={{ fontSize: 12, opacity: 0.9 }}>
                                      {p.leadtime} วัน
                                    </div>
                                  ) : null}
                                </div>
                              </div>
                            </CardBody>
                          </Card>
                        ))}
                      </div>
                    </CardBody>
                  </Card>
                </Col>

                {/* Right: Cart */}
                <Col xs="12" md="12" lg={4} className="px-0 px-lg-2">
                  <Card className="shadow-sm">
                    <div
                      className="position-sticky cart-sticky"
                      style={{ top: 72, width: "100%" }}
                    >
                      <CardBody>
                        <h5 className="mb-3">ตะกร้าแพ็คเกจ</h5>

                        {selectedCustomerPackage && (
                          <div
                            className="mb-2"
                            style={{ fontSize: 12, color: "#475569" }}
                          >
                            ลูกค้าที่เลือก:{" "}
                            <strong>
                              {selectedCustomerPackage.code} -{" "}
                              {selectedCustomerPackage.name}
                            </strong>
                          </div>
                        )}

                        <ListGroup className="mb-3">
                          {cartPackage.length === 0 && (
                            <ListGroupItem className="text-center text-muted">
                              ยังไม่มีแพ็คเกจ
                            </ListGroupItem>
                          )}

                          {cartPackage.map((p) => (
                            <ListGroupItem
                              key={p.id}
                              className="mb-2"
                              style={{
                                borderRadius: 12,
                                border: "1px solid #f1f1f1",
                                padding: "0.9rem 1rem",
                              }}
                            >
                              <div className="d-flex justify-content-between align-items-center">
                                <div>
                                  <div style={{ fontWeight: 700 }}>
                                    {p.name}
                                  </div>
                                  <div
                                    style={{ fontSize: 12, color: "#6b7280" }}
                                  >
                                    จำนวนซื้อ {p.qty} ชุด
                                  </div>
                                </div>

                                <div
                                  className="d-flex align-items-center"
                                  style={{ gap: 10 }}
                                >
                                  <div
                                    style={{
                                      textAlign: "right",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {p.price.toLocaleString()} ฿ / ชุด
                                  </div>
                                  <Button
                                    color="link"
                                    onClick={() => removePackageItem(p.id)}
                                    title="ลบแพ็คเกจนี้"
                                    style={{ padding: 0, lineHeight: 0 }}
                                    aria-label="ลบแพ็คเกจนี้"
                                  >
                                    ✕
                                  </Button>
                                </div>
                              </div>
                            </ListGroupItem>
                          ))}
                        </ListGroup>

                        <div
                          className="p-2 rounded mb-3"
                          style={{
                            background: "#f1f5ff",
                            border: "1px dashed #bcd1ff",
                          }}
                        >
                          <div className="d-flex justify-content-between">
                            <span>ยอดรวม</span>
                            <strong>{totalPackage.toLocaleString()} ฿</strong>
                          </div>
                        </div>

                        <Button
                          block
                          onClick={handlePayPackages}
                          disabled={paying || cartPackage.length === 0}
                          style={{
                            height: 48,
                            fontWeight: 700,
                            backgroundColor: "#1171ef",
                            color: "#fff",
                            border: "none",
                          }}
                        >
                          {paying ? (
                            <span className="spinner-border spinner-border-sm" />
                          ) : (
                            "ชำระเงิน"
                          )}
                        </Button>
                      </CardBody>
                    </div>
                  </Card>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Container>

        {/* Modal: เพิ่มลูกค้า */}
        <Modal
          isOpen={isAddModalOpen}
          toggle={toggleAddModal}
          size="lg"
          centered
          backdrop="static"
          keyboard={false}
        >
          <ModalHeader toggle={toggleAddModal}>เพิ่มลูกค้า</ModalHeader>
          <ModalBody style={{ width: "100%", padding: "1.5rem" }}>
            <Form onSubmit={handleAddCustomerSubmit}>
              <div className="pl-lg-4">
                <Row className="mb-3">
                  <Col lg="12">
                    <div style={{ display: "flex", gap: 8 }}>
                      <Button
                        size="sm"
                        color={
                          customerType === "individual"
                            ? "primary"
                            : "secondary"
                        }
                        outline={customerType !== "individual"}
                        className="rounded-pill"
                        type="button"
                        onClick={() => setCustomerType("individual")}
                      >
                        บุคคลธรรมดา
                      </Button>
                      <Button
                        size="sm"
                        color={
                          customerType === "juristic" ? "primary" : "secondary"
                        }
                        outline={customerType !== "juristic"}
                        className="rounded-pill"
                        type="button"
                        onClick={() => setCustomerType("juristic")}
                      >
                        นิติบุคคล
                      </Button>
                    </div>
                  </Col>
                </Row>

                <Row>
                  <Col lg="6">
                    <FormGroup>
                      <label className="form-control-label required">
                        {isCompany ? "ชื่อบริษัท / นิติบุคคล" : "ชื่อลูกค้า"}
                      </label>
                      <Input
                        className="form-control-alternative"
                        type="text"
                        name="cmName"
                        value={customerForm.cmName}
                        onChange={handleChangeCustomerForm}
                        required
                      />
                    </FormGroup>
                  </Col>
                  <Col lg="6">
                    <FormGroup>
                      <label className="form-control-label required">
                        เบอร์โทร
                      </label>
                      <Input
                        className="form-control-alternative"
                        type="text"
                        name="cmMobile"
                        value={customerForm.cmMobile}
                        onChange={handleChangeCustomerForm}
                        required
                      />
                    </FormGroup>
                  </Col>
                </Row>
              </div>

              <div className="text-right mt-4">
                <Button
                  color="primary"
                  type="submit"
                  style={{ minWidth: 120 }}
                  disabled={submittingCustomer}
                >
                  {submittingCustomer ? (
                    <span className="spinner-border spinner-border-sm" />
                  ) : (
                    "บันทึก"
                  )}
                </Button>
              </div>
            </Form>
          </ModalBody>
        </Modal>
      </div>
    </>
  );
};

export default OrderBuyPackage;
